import { useState, useCallback, useRef } from 'react'
import {
  useQuery,
  useInfiniteQuery,
  useQueryClient,
  type QueryFunctionContext,
  type PlaceholderDataFunction,
} from '@tanstack/react-query'

export interface PaginatedResponse<T> {
  data: T[]
  max_page: number
  has_next: boolean
  count: number
}

export interface QueryParams {
  page: number
  pageSize: number
  search: string
  filters: Record<string, string[]>
  fields?: string
  expand?: string
  ordering?: string
  [key: string]: unknown
}

export interface UseTableStateOptions<T> {
  queryKey: string | string[]
  queryFn: (params: QueryParams) => Promise<PaginatedResponse<T>>
  itemsPerPage?: number
  fields?: string
  expand?: string
  enabled?: boolean
  staleTime?: number
  placeholderData?: T[] | PlaceholderDataFunction<T[]>
  extraParams?: Record<string, unknown>
  initialPage?: number
  initialFilters?: Record<string, string[]>
}

export function useTableState<T = Record<string, unknown>>({
  queryKey: queryKeyProp,
  queryFn,
  itemsPerPage = 15,
  fields,
  expand,
  enabled = true,
  staleTime = 30 * 1000,
  placeholderData,
  extraParams = {},
  initialPage = 1,
  initialFilters = {},
}: UseTableStateOptions<T>) {
  const [page, setPageRaw] = useState(initialPage)
  const [search, setSearchRaw] = useState('')
  const [filters, setFiltersRaw] = useState<Record<string, string[]>>(initialFilters)
  const [selectedRows, setSelectedRows] = useState<(string | number)[]>([])
  const [sortBy, setSortByRaw] = useState<string>('')

  const queryClient = useQueryClient()
  const extraParamsRef = useRef(extraParams)
  extraParamsRef.current = extraParams

  const cachedTotalPages = useRef(1)
  const cachedTotalItems = useRef(0)

  const baseKey = Array.isArray(queryKeyProp) ? queryKeyProp : [queryKeyProp]

  const setPage = useCallback((value: number) => {
    setPageRaw(Math.max(1, value))
  }, [])

  const setSearch = useCallback((value: string) => {
    setSearchRaw(value)
    setPageRaw(1)
  }, [])

  const setSortBy = useCallback((value: string) => {
    setSortByRaw(value)
    setPageRaw(1)
  }, [])

  const setFilters = useCallback((value: Record<string, string[]>) => {
    setFiltersRaw(value)
    setPageRaw(1)
  }, [])

  const updateFilter = useCallback((key: string, values: string[]) => {
    setFiltersRaw((prev) => {
      const next = { ...prev }
      if (values.length === 0) delete next[key]
      else next[key] = values
      return next
    })
    setPageRaw(1)
  }, [])

  const handleSelectRow = useCallback((id: string | number, isChecked: boolean) => {
    setSelectedRows((prev) =>
      isChecked ? [...prev, id] : prev.filter((rowId) => rowId !== id)
    )
  }, [])

  const handleSelectAll = useCallback((isChecked: boolean, ids: (string | number)[]) => {
    setSelectedRows(isChecked ? ids : [])
  }, [])

  const clearSelection = useCallback(() => setSelectedRows([]), [])

  const queryParams: QueryParams = {
    page,
    pageSize: itemsPerPage,
    search,
    filters,
    fields,
    expand,
    ordering: sortBy || undefined,
    ...extraParamsRef.current,
  }

  const query = useQuery({
    queryKey: [...baseKey, page, search, filters, sortBy, extraParamsRef.current],
    queryFn: () => queryFn(queryParams),
    staleTime,
    enabled,
    placeholderData: placeholderData as any,
  })

  const prefetchNextPage = useCallback(() => {
    const currentCount = (query.data?.data ?? []).length
    if (currentCount < itemsPerPage) return
    const nextPage = page + 1
    const maxPage = query.data?.max_page ?? 1
    if (nextPage > maxPage) return

    queryClient.prefetchQuery({
      queryKey: [...baseKey, nextPage, search, filters, sortBy, extraParamsRef.current],
      queryFn: () =>
        queryFn({
          page: nextPage,
          pageSize: itemsPerPage,
          search,
          filters,
          fields,
          expand,
          ordering: sortBy || undefined,
          ...extraParamsRef.current,
        }),
      staleTime,
    })
  }, [query.data, page, search, filters, sortBy, itemsPerPage, queryClient, baseKey, fields, expand, staleTime])

  /** Warm page 1 of another filter set (e.g. a status tab under the pointer), with the same key
   * `setFilters` would then read, so switching to it shows its rows at once. */
  const prefetchFilters = useCallback((nextFilters: Record<string, string[]>) => {
    void queryClient.prefetchQuery({
      queryKey: [...baseKey, 1, search, nextFilters, sortBy, extraParamsRef.current],
      queryFn: () =>
        queryFn({
          page: 1,
          pageSize: itemsPerPage,
          search,
          filters: nextFilters,
          fields,
          expand,
          ordering: sortBy || undefined,
          ...extraParamsRef.current,
        }),
      staleTime,
    })
  }, [search, sortBy, itemsPerPage, queryClient, baseKey, fields, expand, staleTime, queryFn])

  if (query.data) {
    cachedTotalPages.current = query.data.max_page
    cachedTotalItems.current = query.data.count
  }

  return {
    search,
    setSearch,
    filters,
    setFilters,
    updateFilter,
    page,
    setPage,
    itemsPerPage,
    sortBy,
    setSortBy,
    data: (query.data?.data ?? []) as T[],
    totalPages: query.data?.max_page ?? cachedTotalPages.current,
    hasMore: query.data?.has_next ?? false,
    totalItems: query.data?.count ?? cachedTotalItems.current,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
    isPlaceholderData: query.isPlaceholderData,
    selectedRows,
    handleSelectRow,
    handleSelectAll,
    clearSelection,
    query,
    prefetchNextPage,
    prefetchFilters,
    queryParams,
    queryKey: [...baseKey, page, search, filters, sortBy, extraParamsRef.current] as const,
  }
}

/* ─── useTableFilter ───────────────────────────────────────────────
   Helper for filter dropdowns that need infinite-scroll data.
   Wraps useInfiniteQuery with the standard pageParam pattern.
──────────────────────────────────────────────────────────────────── */

interface UseTableFilterOptions<T> {
  queryKey: string | string[]
  queryFn: (ctx: QueryFunctionContext) => Promise<PaginatedResponse<T>>
  search?: string
  enabled?: boolean
  staleTime?: number
  pageSize?: number
}

export function useTableFilter<T = Record<string, unknown>>({
  queryKey,
  queryFn,
  search = '',
  enabled = true,
  staleTime = 30 * 1000,
  pageSize = 10,
}: UseTableFilterOptions<T>) {
  const infiniteQuery = useInfiniteQuery({
    queryKey: Array.isArray(queryKey) ? [...queryKey, pageSize, search] : [queryKey, pageSize, search],
    queryFn,
    initialPageParam: 1,
    getNextPageParam: (lastPage: PaginatedResponse<T>, allPages: PaginatedResponse<T>[]) =>
      lastPage?.has_next ? allPages.length + 1 : undefined,
    enabled,
    staleTime,
  })

  const flatList = infiniteQuery.data?.pages.flatMap((page) => page.data) ?? []

  return {
    data: flatList,
    fetchNextPage: infiniteQuery.fetchNextPage,
    hasNextPage: infiniteQuery.hasNextPage,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    isLoading: infiniteQuery.isLoading,
    isFetching: infiniteQuery.isFetching,
    isError: infiniteQuery.isError,
    refetch: infiniteQuery.refetch,
  }
}

/* ─── useTableView ─────────────────────────────────────────────────
   For tables that switch between views (e.g. active/disabled tabs).
   Manages per-view filter overrides so switching tabs resets page.
──────────────────────────────────────────────────────────────────── */

interface ViewConfig {
  key: string
  filters: Record<string, string[]>
}

export function useTableView(views: ViewConfig[], initialView?: string) {
  const [activeView, setActiveViewRaw] = useState(initialView ?? views[0]?.key ?? '')

  const currentView = views.find((v) => v.key === activeView) ?? views[0]

  const setActiveView = useCallback(
    (key: string) => {
      setActiveViewRaw(key)
    },
    []
  )

  return {
    activeView,
    setActiveView,
    viewFilters: currentView?.filters ?? {},
  }
}

export type TableState<T = Record<string, unknown>> = ReturnType<typeof useTableState<T>>
